import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import { match, RouteComponentProps } from 'react-router-dom';

// Components
import App from '../../../../components/app';
import ErrorMessage from '../../../../components/error-message';
import EnhancedForm from '../../../../components/form';
import { FormDatas } from '../../../../local-types';
// Modules
import { Price, Service, Set, SetByID, TParams } from '../../../../redux/reducers/api-types';
import { ErrorPayload, selectors as errorSelectors } from '../../../../redux/reducers/errors';
import {
    actions as pricingActions,
    selectors as pricingSelectors,
    types as pricingTypes,
} from '../../../../redux/reducers/pricing';
import { selectors as pendingSelectors } from '../../../../redux/reducers/pendingOperations';
// Ressources
import withGlobalStyles from '../../../../utils/global-styles';
import { createPath, paths } from '../../../paths';
import { AccountRight, RightRank } from '../../../../utils/userUtils';
const queryString = require('query-string');

interface PricingPricesAddProps extends WithTranslation, RouteComponentProps {
    match: match<TParams>;
    globalClasses: any;
    prices: Price[];
    sets: SetByID;
    services: Service[];
    getPricesAddError: ErrorPayload;
    isSavingPricingSet: boolean;
    getPricingSet: (id: string) => void;
    getPricingSets: () => void;
    getServices: () => void;
    createPricingSet: (payload: any, onCreated?: Function) => void;
    editPricingSet: (id: string, payload: any) => void;
    createGroupToPricingSets: (idc, pricing_set, payload, callback?) => any;
    push: (path: string) => void;
}

interface PricingPricesAddState {
    formData: any;
}

class PricingPricesAddPage extends React.Component<PricingPricesAddProps, PricingPricesAddState> {
    state = {
        formData: {},
    };

    /**
     * Creating a sheet for a departement, rather than a standalone one.
     *
     * The form is reached from a departement's tariff card, which knows which
     * company sheet is being overridden and for which service, so both arrive
     * prefilled: a group sheet with no parent would inherit nothing and price
     * the departement on an empty sheet.
     */
    groupMode = () => {
        const { group_id, parent_id, service_id, company_id } = queryString.parse(this.props.location.search);
        if (!group_id || !parent_id) {
            return null;
        }
        return { group_id, parent_id, service_id, company_id };
    };

    componentDidMount() {
        const { getPricingSets, getPricingSet, getServices } = this.props;
        const { id } = queryString.parse(this.props.location.search);
        if (id) {
            getPricingSet(id);
        }
        getPricingSets();
        getServices();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.match.params.id !== (this.props.match.params as any).id) {
            const id: string = this.props.match.params.id;
            this.props.getPricingSet(id);
        }
    }

    static getDerivedStateFromProps(props: PricingPricesAddProps, state: PricingPricesAddState) {
        const { id, group_id, parent_id, service_id } = queryString.parse(props.location.search);
        const { sets } = props;

        // Creating for a group: seed the parent and service once, then leave
        // the form alone so the user's own edits survive re-renders.
        if (!id && group_id && parent_id && !(state.formData as any).parent_id) {
            return { formData: { ...state.formData, parent_id, service_id } };
        }

        const needUpdate = !state.formData.id || state.formData.id !== id;
        if (sets && needUpdate) {
            if (sets[id]) {
                return { formData: { ...sets[id] }.data };
            }
        }
        return null;
    }

    generateInputs = (): FormDatas[] => {
        const { t, prices, services, location } = this.props;
        const { id } = queryString.parse(location.search);
        const group = this.groupMode();
        const inputs: FormDatas[] = [
            {
                title: t(id ? 'sets.edit_price' : 'sets.new_price'),
                datas: [
                    { id: 'name', label: t('common:form.lastname'), required: true },
                    {
                        id: 'parent_id',
                        label: t('sets.parent'),
                        type: 'picker',
                        // Mandatory and fixed for a group sheet: it is the
                        // company sheet being overridden, chosen on the page
                        // that sent us here.
                        required: !!group,
                        disable: !!group,
                    },
                    {
                        id: 'service_id',
                        label: t('sets.service'),
                        type: 'picker',
                        required: true,
                        disable: !!group?.service_id,
                    },
                ],
            },
        ];

        if (prices) {
            inputs[0].datas.find((d) => d.id === 'parent_id').options = prices.map((r) => {
                return { id: r.id, name: r.name };
            });
        }
        if (services) {
            inputs[0].datas.find((d) => d.id === 'service_id').options = services.map((r) => {
                return { id: r.id, name: r.name };
            });
        }

        return inputs;
    };

    onSubmitPrice = (formData: any, event) => {
        const { id } = queryString.parse(this.props.location.search);

        // let data = new FormData(event.target);
        this.setState({
            formData: formData,
        });

        if (id) {
            this.props.editPricingSet(id, formData);
            return;
        }

        const group = this.groupMode();
        if (!group) {
            this.props.createPricingSet(formData);
            return;
        }

        // Create then attach. Both halves are gated on different rights, which
        // is why the control that opens this form demands both: a sheet created
        // without the attach right is an orphan nobody is told about.
        this.props.createPricingSet(formData, (_dispatch, response) => {
            const created = response?.payload?.data;
            if (!created?.id) {
                return;
            }
            // The created row carries service_id, not the nested service the
            // departement page renders a row from.
            const service = this.props.services?.find((x) => x.id === created.service_id);
            const back = group.company_id
                ? createPath(paths.companiesDetails, { id: group.company_id })
                : paths.pricingManagePrices;

            // Already bound by connect, so it dispatches itself - wrapping it
            // in the dispatch above would dispatch fetchAction's promise.
            this.props.createGroupToPricingSets(
                group.company_id,
                { ...created, service },
                { pricing_set_id: created.id, group_id: group.group_id },
                () => this.props.push(back),
            );
        });
    };

    render() {
        const { prices, sets, getPricesAddError, isSavingPricingSet } = this.props;

        const { id } = queryString.parse(this.props.location.search);
        const payload = sets[id] || { data: {} as Set };
        const formPayload = this.state.formData;

        let dataLoaded: any = true;
        if (id) {
            dataLoaded = payload.data;
        }

        // The save exercises both rights - pricing gates sheet creation on the
        // first and the link on the second - so a user holding only one would
        // get half a result.
        const group = this.groupMode();
        const needRights = group
            ? [
                  { name: AccountRight.PRICING_MGMT, mode: RightRank.W },
                  { name: AccountRight.COMPANY_PRICING, mode: RightRank.W },
              ]
            : { name: AccountRight.PRICING_MGMT, mode: RightRank.W };

        return (
            <App needRights={needRights}>
                <ErrorMessage payload={getPricesAddError} />
                {dataLoaded && prices && formPayload && (
                    <>
                        <EnhancedForm
                            payload={formPayload}
                            inputs={this.generateInputs()}
                            backRoute={paths.pricingManagePrices}
                            onSubmit={this.onSubmitPrice}
                            loader={isSavingPricingSet}
                        />
                    </>
                )}
            </App>
        );
    }
}

const PricingPricesAddPageWithTranslation = withGlobalStyles(
    withTranslation(['pricing', 'common'])(PricingPricesAddPage),
);

const mapStateToProps = (state) => ({
    sets: pricingSelectors.sets(state),
    prices: pricingSelectors.prices(state),
    services: pricingSelectors.services(state),
    getPricesAddError: errorSelectors.getPricesAddError(state),
    // editPricingSet dispatches EDIT_AREA
    isSavingPricingSet: pendingSelectors.isPending(
        state,
        pricingTypes.CREATE_PRICING_SET,
        pricingTypes.EDIT_AREA,
        pricingTypes.CREATE_GROUP_PRICING_SET,
    ),
});

const mapDispatchToProps = {
    getPricingSet: pricingActions.getPricingSet,
    getPricingSets: pricingActions.getPricingSets,
    getServices: pricingActions.getServices,
    createPricingSet: pricingActions.createPricingSet,
    editPricingSet: pricingActions.editPricingSet,
    createGroupToPricingSets: pricingActions.createGroupToPricingSets,
    push,
};

export default connect(mapStateToProps, mapDispatchToProps)(PricingPricesAddPageWithTranslation);
